import { HelpCircle, Info as InfoIcon } from 'lucide-react'
import { Tooltip } from '../../../../components/ui/Tooltip'
import { useScopeStore } from '../../store'
import { getGuidance } from '../../guidance'
import { getFieldById, getGroupById, getSectionById } from '../../schema'
import {
  KYC_VENDORS,
  type KycConfig,
} from '../../types'
import { RequiredMark, SectionHeader, TriStateRadio, normalizeTriState } from './shared'

export function AdvancedKycSection() {
  const {
    scope,
    updateKycConfig,
  } = useScopeStore()
  const { kycConfig } = scope
  const disabled = scope.finalized

  // --- KYC fields ---
  const complianceDataSection = getSectionById('kyc_compliance_data_requirements')
  const additionalConfigSection = getSectionById('kyc_additional_configuration')

  const vendorGroup = getGroupById('kyc_vendor_group')
  const matchThresholdsGroup = getGroupById('kyc_match_thresholds_group')
  const additionalConfigurationGroup = getGroupById('kyc_additional_configuration_group')

  const reportingEntityField = getFieldById('kyc_is_reporting_entity')
  const vendorField = getFieldById('kyc_vendor')
  const otherVendorField = getFieldById('kyc_other_vendor')
  const complianceFrameworkField = getFieldById('kyc_compliance_framework')
  const specificRegulatoryRequirementsField = getFieldById('kyc_specific_regulatory_requirements')
  const nameMatchingToleranceField = getFieldById('kyc_name_matching_tolerance')
  const dobMatchingToleranceField = getFieldById('kyc_dob_matching_tolerance')
  const addressMatchingToleranceField = getFieldById('kyc_address_matching_tolerance')
  const passFailCriteriaField = getFieldById('kyc_pass_fail_criteria')
  const specialRequirementsField = getFieldById('kyc_special_requirements')

  const reportingEntityValue = normalizeTriState(kycConfig.isReportingEntity)

  return (
    <>
      {/* Reporting Entity Status */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-[var(--text-primary)]">
              {reportingEntityField?.formLabel || 'Is this customer a reporting entity under AML/CTF regulations?'}
              <RequiredMark />
              <Tooltip
                content={
                  getGuidance('KYC', 'Reporting entity (AML/CTF)') || reportingEntityField?.tooltip || ''
                }
              >
                <span className="ml-2 inline-flex align-middle">
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </span>
              </Tooltip>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              <span className="text-primary font-bold mr-1">*</span>
              {reportingEntityField?.formDescription || "Required for completion. Choose Yes/No/Don't know."}
            </p>
          </div>
          <TriStateRadio
            name="reporting-entity"
            value={reportingEntityValue}
            onChange={(v) => updateKycConfig('isReportingEntity', v)}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Vendor Selection */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">
          {vendorGroup?.formTitle || 'KYC Vendor'}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-2">
              {vendorField?.formLabel || 'Primary KYC Vendor'}
              <RequiredMark />
            </label>
            <select
              className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={kycConfig.vendor}
              onChange={(e) => updateKycConfig('vendor', e.target.value)}
              disabled={disabled}
            >
              <option value="">Select vendor</option>
              {(vendorField?.options || KYC_VENDORS.map((v) => ({ value: v.id, label: v.label }))).map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              <span className="text-primary font-bold mr-1">*</span>
              {vendorGroup?.helperText || 'Required for completion.'}
            </p>
          </div>
          {kycConfig.vendor === 'other' && (
            <div>
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                {otherVendorField?.formLabel || 'Other Vendor Name'}
              </label>
              <input
                type="text"
                className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                placeholder={otherVendorField?.placeholder || 'Enter vendor name'}
                value={kycConfig.otherVendor}
                onChange={(e) => updateKycConfig('otherVendor', e.target.value)}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      </div>

      {/* Compliance & data requirements */}
      <SectionHeader
        title={complianceDataSection?.formTitle || 'Compliance & data requirements'}
        subtitle={
          complianceDataSection?.formDescription ||
          'Define regulatory requirements, required data, matching tolerances, and pass/fail criteria'
        }
      />

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="text-sm font-medium text-[var(--text-primary)]">
            {complianceFrameworkField?.formLabel || 'Compliance Framework'}
          </h4>
          <Tooltip content={getGuidance('KYC', 'Compliance framework') || complianceFrameworkField?.tooltip || ''}>
            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
        </div>
        <select
          className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
          value={kycConfig.complianceFramework}
          onChange={(e) => updateKycConfig('complianceFramework', e.target.value as KycConfig['complianceFramework'])}
          disabled={disabled}
        >
          {(complianceFrameworkField?.options || [
            { value: 'unknown', label: 'Unknown / To Be Determined' },
            { value: 'safe_harbour_2x2', label: 'AML/CTF Safe Harbour (2+2)' },
            { value: 'standard_1x1', label: 'AML/CTF Standard (1+1)' },
            { value: 'custom', label: 'Custom regulatory requirement' },
            { value: 'none', label: 'No specific regulatory requirement' },
          ]).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <label className="text-sm font-medium text-[var(--text-primary)]">
            {specificRegulatoryRequirementsField?.formLabel || 'Specific Regulatory Requirements'}
          </label>
          <Tooltip
            content={
              getGuidance('KYC', 'Specific regulatory requirements') ||
              specificRegulatoryRequirementsField?.tooltip ||
              ''
            }
          >
            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
        </div>
        <textarea
          className="w-full h-24 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
          placeholder={
            specificRegulatoryRequirementsField?.placeholder ||
            'e.g., Must comply with AUSTRAC reporting obligations, FINTRAC requirements, etc.'
          }
          value={kycConfig.specificRegulatoryRequirements}
          onChange={(e) => updateKycConfig('specificRegulatoryRequirements', e.target.value)}
          disabled={disabled}
        />
      </div>

      {/* Match Thresholds & Tolerances */}
      <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-[var(--text-primary)]">
            {matchThresholdsGroup?.formTitle || 'Match Thresholds & Tolerances'}
          </h4>
          <Tooltip
            content={
              getGuidance('KYC', 'Match thresholds & tolerances') || matchThresholdsGroup?.tooltip || ''
            }
          >
            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {nameMatchingToleranceField?.formLabel || 'Name Matching Tolerance'}
            </label>
            <select
              className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={kycConfig.nameMatchingTolerance}
              onChange={(e) => updateKycConfig('nameMatchingTolerance', e.target.value as KycConfig['nameMatchingTolerance'])}
              disabled={disabled}
            >
              {(nameMatchingToleranceField?.options || [
                { value: 'tbd', label: 'To Be Determined' },
                { value: 'exact_100', label: 'Exact Match (100%)' },
                { value: 'very_high_95', label: 'Very High (95%)' },
                { value: 'high_90', label: 'High (90%)' },
                { value: 'standard_85', label: 'Standard (85%)' },
                { value: 'medium_70_80', label: 'Medium (70-80%)' },
              ]).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {dobMatchingToleranceField?.formLabel || 'Date of Birth Tolerance'}
            </label>
            <select
              className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={kycConfig.dobMatchingTolerance}
              onChange={(e) => updateKycConfig('dobMatchingTolerance', e.target.value as KycConfig['dobMatchingTolerance'])}
              disabled={disabled}
            >
              {(dobMatchingToleranceField?.options || [
                { value: 'tbd', label: 'To Be Determined' },
                { value: 'exact', label: 'Exact Match Required' },
                { value: 'minor_variations', label: 'Allow Minor Variations' },
              ]).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {addressMatchingToleranceField?.formLabel || 'Address Matching Tolerance'}
            </label>
            <select
              className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={kycConfig.addressMatchingTolerance}
              onChange={(e) =>
                updateKycConfig('addressMatchingTolerance', e.target.value as KycConfig['addressMatchingTolerance'])
              }
              disabled={disabled}
            >
              {(addressMatchingToleranceField?.options || [
                { value: 'tbd', label: 'To Be Determined' },
                { value: 'exact_100', label: 'Exact Match (100%)' },
                { value: 'very_high_95', label: 'Very High (95%)' },
                { value: 'high_90', label: 'High (90%)' },
                { value: 'standard_85', label: 'Standard (85%)' },
                { value: 'partial', label: 'Partial Match Allowed' },
              ]).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">
              {passFailCriteriaField?.formLabel || 'Pass/Fail Criteria Definition'}
            </label>
            <Tooltip
              content={
                getGuidance('KYC', 'Pass/fail criteria definition') || passFailCriteriaField?.tooltip || ''
              }
            >
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </Tooltip>
          </div>
          <textarea
            className="w-full h-24 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
            placeholder={
              passFailCriteriaField?.placeholder ||
              'e.g., All data elements must match at 85% or higher across minimum 2 sources'
            }
            value={kycConfig.passFailCriteria}
            onChange={(e) => updateKycConfig('passFailCriteria', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
      </div>

      {/* Additional configuration */}
      <SectionHeader
        title={additionalConfigSection?.formTitle || 'Additional configuration'}
        subtitle={
          additionalConfigSection?.formDescription ||
          'Additional requirements or special considerations for KYC.'
        }
      />

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)]">
          {additionalConfigurationGroup?.formTitle || 'Additional Configuration'}
        </h4>

        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            {specialRequirementsField?.formLabel || 'Special Requirements or Considerations'}
          </label>
          <textarea
            className="w-full h-24 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
            placeholder={
              specialRequirementsField?.placeholder ||
              'e.g., VIP customers require enhanced verification; customers under 18 have different document requirements; require manual review for PEP/sanctions hits, partial matches below threshold, or repeated failed attempts'
            }
            value={kycConfig.specialRequirements}
            onChange={(e) => updateKycConfig('specialRequirements', e.target.value)}
            disabled={disabled}
          />
        </div>

        {/* KYC Aging Configuration */}
        <div className="space-y-4 mt-6">
          <div className="pt-2">
            <h4 className="text-base font-semibold text-[var(--text-primary)]">KYC Data Aging</h4>
          </div>

          <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <InfoIcon size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-[var(--text-primary)]">
              <p className="mb-2">
                KYC aging determines how long verification results remain valid before requiring re-verification.
                When a check workflow starts, the system reloads past results and applies aging rules.
              </p>
              <p className="mb-2 font-medium">Default: 12 months (1 year)</p>
              <p className="text-[var(--text-muted)] text-[10px] leading-relaxed">
                Note: Results are automatically invalidated when key data changes (address, DOB, ID). If name changes,
                all previous results are invalidated as the system treats it as a new person.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)] cursor-pointer">
              <input
                type="checkbox"
                checked={kycConfig.agingEnabled}
                onChange={(e) => updateKycConfig('agingEnabled', e.target.checked)}
                disabled={disabled}
                className="rounded border-[var(--border-color)] bg-[var(--bg-primary)] text-blue-500 focus:ring-blue-500"
              />
              Use Custom Aging Period
            </label>

            {kycConfig.agingEnabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Aging Period (Months)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={kycConfig.agingPeriodMonths}
                      onChange={(e) => updateKycConfig('agingPeriodMonths', parseInt(e.target.value) || 12)}
                      disabled={disabled}
                      className="w-32 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                    />
                    <span className="text-sm text-[var(--text-secondary)]">months</span>
                    <span className="text-xs text-[var(--text-muted)]">
                      ({Math.round((kycConfig.agingPeriodMonths / 12) * 10) / 10}{' '}
                      {kycConfig.agingPeriodMonths === 12 ? 'year' : 'years'})
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-2">
                    KYC results older than {kycConfig.agingPeriodMonths} months will require re-verification
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Additional Aging Configuration (Optional)
                  </label>
                  <textarea
                    value={kycConfig.agingCustomConfig}
                    onChange={(e) => updateKycConfig('agingCustomConfig', e.target.value)}
                    disabled={disabled}
                    placeholder="Enter any custom aging rules or specific requirements for different data types (e.g., DOB, address)"
                    rows={3}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-sm"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Specify if you need different aging periods for specific data types or special requirements
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  )
}
